import { Environment } from 'environment.js'
import { get, post, put, remove } from 'Repository/index.js'
import type { Observable } from 'rxjs'
import type { IUser } from 'Model/index.js'
import type { OrganizationProfile } from '../../Generated/OrganizationProfile.ts'
import type { IBulkResponse } from 'Framework/Repository/Response/IBulkResponse.js'
import type { OrganizationType } from 'Model/Organization/OrganizationType.js'
import type { SampleConfigurationTypeKey } from '../../Generated/SampleConfigurationType.js'
import type { RedlistModeKey } from '../../Generated/RedlistMode.js'
import type { ProcessingTypeKey } from '../../Generated/ProcessingType.ts'

/**
 * List organizations with pagination and filtering
 */
export const listOrganizations = (
  data: { page: number; size: number; filter: string },
  user?: IUser
): Observable<IBulkResponse<OrganizationProfile>> => {
  return get(`${Environment.API_URL}/api/organizations/`, data, user)
}

export const listRelatedOrganizations = (user?: IUser): Observable<OrganizationProfile[]> => {
  return get(`${Environment.API_URL}/api/organizations/related`, {}, user)
}

/**
 * Get members of an organization
 */
export const getOrganizationMembers = (data: { organizationId: string }, user?: IUser): Observable<IUser[]> => {
  return get(`${Environment.API_URL}/api/organizations/${data.organizationId}/members`, {}, user)
}

/**
 * Update a member of an organization
 */
export const updateOrganizationMember = (
  data: {
    organizationId: string
    memberId: string
    isAdmin?: boolean
    isContact?: boolean
  },
  user?: IUser
): Observable<IUser> => {
  return put(
    `${Environment.API_URL}/api/organizations/${data.organizationId}/members/${data.memberId}`,
    {
      isAdmin: data.isAdmin,
      isContact: data.isContact
    },
    user
  )
}

export const updateOrganizationLogo = (
  data: {
    logo: File
  },
  user?: IUser
): Observable<{ key: string }> => {
  const formData = new FormData()
  formData.append('file', data.logo)
  return post(`${Environment.API_URL}/api/organizations/logo`, formData, user)
}

export const deleteOrganizationLogo = (user?: IUser): Observable<string> => {
  return remove(`${Environment.API_URL}/api/organizations/logo`, user)
}

/**
 * Update an organization
 */
export const updateOrganization = (
  data: {
    organizationId: string
    name: string
    isPublic: boolean
    description: string
    type: OrganizationType
    homepage?: string
    country?: string
    specialities?: string[]
    contactEmail?: string
    defaultSampleConfigurationType: SampleConfigurationTypeKey
    defaultRedlistMode: RedlistModeKey
    defaultProcessingType: ProcessingTypeKey
  },
  user?: IUser
): Observable<OrganizationProfile> => {
  return put(
    `${Environment.API_URL}/api/organizations/${data.organizationId}`,
    {
      name: data.name,
      description: data.description,
      type: data.type,
      isPublic: data.isPublic,
      homepage: data.homepage,
      country: data.country,
      specialities: data.specialities,
      contactEmail: data.contactEmail,
      sampleConfigurationType: data.defaultSampleConfigurationType,
      redlistMode: data.defaultRedlistMode,
      processingType: data.defaultProcessingType
    },
    user
  )
}

/**
 * Get an organization by ID
 */
export const getOrganization = (data: { organizationId: string }, user?: IUser): Observable<OrganizationProfile> => {
  return get(`${Environment.API_URL}/api/organizations/${data.organizationId}`, {}, user)
}
